import { useState } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';

import { IconButton, Tooltip, Icon } from '@folio/stripes/components';

import css from './SourcesCollapse.css';

const propTypes = {
  sources: PropTypes.arrayOf(PropTypes.string),
  sourceCount: PropTypes.number,
};

const defaultProps = {
  sources: [],
  sourceCount: 0,
};

const SourcesCollapse = ({ sources, sourceCount }) => {
  const intl = useIntl();
  const [isExpanded, setIsExpanded] = useState(false);

  if (!sources || sources.length === 0) {
    return null;
  }

  const getFileIcon = (filename) => {
    const extension = filename.split('.').pop()?.toLowerCase();
    
    switch (extension) {
      case 'pdf':
        return 'document';
      case 'docx':
      case 'doc':
        return 'document';
      case 'md':
      case 'txt':
        return 'document-text';
      default:
        return 'document';
    }
  };

  const getFileType = (filename) => {
    const extension = filename.split('.').pop()?.toLowerCase();
    return extension ? extension.toUpperCase() : 'FILE';
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const tooltipText = isExpanded
    ? intl.formatMessage(
        { id: 'ui-plugin-smart-help.sources.hide' },
        { count: sourceCount }
      )
    : intl.formatMessage(
        { id: 'ui-plugin-smart-help.sources.show' },
        { count: sourceCount }
      );

  return (
    <div className={css.sourcesContainer}>
      <Tooltip 
        id={`sources-tooltip-${Date.now()}`}
        text={tooltipText}
        placement="top"
      >
        {({ ref, ariaIds }) => (
          <IconButton
            ref={ref}
            icon="folder-open"
            onClick={toggleExpanded}
            className={css.toggleButton}
            aria-expanded={isExpanded}
            aria-controls="sources-list"
            {...ariaIds}
          />
        )}
      </Tooltip>

      {isExpanded && (
        <div id="sources-list" className={css.sourcesList}>
          {sources.map((source, index) => (
            <div key={index} className={css.sourceItem}>
              <div className={css.sourceIcon}>
                <Icon icon={getFileIcon(source)} size="small" />
              </div>
              <div className={css.sourceInfo}>
                <span className={css.sourceName}>{source}</span>
                <span className={css.sourceType}>{getFileType(source)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

SourcesCollapse.propTypes = propTypes;
SourcesCollapse.defaultProps = defaultProps;

export default SourcesCollapse;